import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/FriendsSystem.css';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to check if string looks like encoded data
const looksLikeEncodedData = (str) => {
  if (!str || typeof str !== 'string') return false;
  // Check for base64-like patterns or long alphanumeric strings
  if (str.length > 30 && /^[A-Za-z0-9+/=]+$/.test(str)) return true;
  if (str.includes('/') && str.length > 20) return true;
  return false;
};

// Helper to get safe avatar
const getSafeAvatar = (avatar) => {
  if (!avatar) return '👤';
  if (looksLikeEncodedData(avatar)) return '👤';
  if (avatar.startsWith('data:image') || avatar.startsWith('http')) return avatar;
  if (avatar.length <= 4) return avatar; // Emoji avatars are usually short
  return '👤';
};

// Helper to get safe display name
const getSafeDisplayName = (friend) => {
  if (!friend) return 'Unknown';
  const name = friend.name;
  if (name && !looksLikeEncodedData(name)) return name;
  if (friend.email && friend.email.includes('@')) {
    const emailName = friend.email.split('@')[0];
    return emailName.replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  return 'Friend';
};

const FriendsSystem = ({ isOpen, onClose }) => {
  const { user, token } = useAuth();
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [onlineNotifications, setOnlineNotifications] = useState([]);

  const loadFriends = useCallback(async () => {
    if (!token) return;
    try {
      // Correct endpoint: /api/friends (not /api/friends/list)
      const response = await fetch(`${BACKEND_URL}/api/friends`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setFriends(data.friends || []);
      }
    } catch (error) {
      logger.error('Error loading friends:', error);
    }
  }, [token]);

  const loadFriendRequests = useCallback(async () => {
    if (!token) return;
    try {
      // Correct endpoint: /api/friends/requests/pending
      const response = await fetch(`${BACKEND_URL}/api/friends/requests/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Transform the received requests to match frontend expected format
        const formattedRequests = (data.received || []).map(req => ({
          id: req.request_id,
          from_name: req.from_user_name,
          from_avatar: req.from_user_avatar,
          from_user_id: req.from_user_id
        }));
        setFriendRequests(formattedRequests);
      }
    } catch (error) {
      logger.error('Error loading friend requests:', error);
    }
  }, [token]);

  // Load data when modal opens
  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      if (!isOpen || !user || !token) return;
      
      // Load friends
      try {
        const response = await fetch(`${BACKEND_URL}/api/friends`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok && isMounted) {
          const data = await response.json();
          setFriends(data.friends || []);
        }
      } catch (error) {
        logger.error('Error loading friends:', error);
      }
      
      // Load friend requests
      try {
        const response = await fetch(`${BACKEND_URL}/api/friends/requests/pending`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok && isMounted) {
          const data = await response.json();
          const formattedRequests = (data.received || []).map(req => ({
            id: req.request_id,
            from_name: req.from_user_name,
            from_avatar: req.from_user_avatar,
            from_user_id: req.from_user_id
          }));
          setFriendRequests(formattedRequests);
        }
      } catch (error) {
        logger.error('Error loading friend requests:', error);
      }
    };
    
    fetchData();
    
    // Poll for updates every 10 seconds
    const interval = isOpen && user ? setInterval(fetchData, 10000) : null;
    
    return () => {
      isMounted = false;
      if (interval) clearInterval(interval);
    };
  }, [isOpen, user, token]);

  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/friends/search?q=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Map user_id to id for frontend compatibility
        const formattedUsers = (data.users || []).map(u => ({
          id: u.user_id,
          name: u.name,
          email: u.email,
          avatar: u.avatar,
          is_friend: u.is_friend,
          has_pending_request: u.has_pending_request
        }));
        setSearchResults(formattedUsers);
      }
    } catch (error) {
      logger.error('Error searching users:', error);
    }
  };

  const sendFriendRequest = async (userEmail) => {
    try {
      // Backend expects receiver_email, not friend_id
      const response = await fetch(`${BACKEND_URL}/api/friends/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ receiver_email: userEmail })
      });
      if (response.ok) {
        alert('Friend request sent!');
        setSearchQuery('');
        setSearchResults([]);
        loadFriendRequests(); // Refresh to show sent request
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to send request');
      }
    } catch (error) {
      logger.error('Error sending friend request:', error);
    }
  };

  const acceptFriendRequest = async (requestId) => {
    try {
      // Correct endpoint: /api/friends/accept/{request_id}
      const response = await fetch(`${BACKEND_URL}/api/friends/accept/${requestId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        loadFriends();
        loadFriendRequests();
      }
    } catch (error) {
      logger.error('Error accepting friend request:', error);
    }
  };

  const rejectFriendRequest = async (requestId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/friends/reject/${requestId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        loadFriendRequests();
      }
    } catch (error) {
      logger.error('Error rejecting friend request:', error);
    }
  };

  const removeFriend = async (friendId) => {
    if (!window.confirm('Remove this friend?')) return;
    
    try {
      // Correct endpoint: DELETE /api/friends/{friend_id}
      const response = await fetch(`${BACKEND_URL}/api/friends/${friendId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        loadFriends();
      }
    } catch (error) {
      logger.error('Error removing friend:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Online Notifications */}
      {onlineNotifications.map((friend, index) => (
        <div key={friend.user_id} className="online-notification" style={{ top: `${20 + index * 80}px` }}>
          <div className="notification-icon">🟢</div>
          <div className="notification-content">
            <strong>{friend.name}</strong> is now online!
          </div>
        </div>
      ))}

      {/* Friends Modal */}
      <div className="friends-overlay" onClick={onClose}>
        <div className="friends-modal" onClick={(e) => e.stopPropagation()}>
          <div className="friends-header">
            <h2>👥 Friends</h2>
            <span className="premium-only">⭐ Premium Feature</span>
            <button className="close-button" onClick={onClose}>×</button>
          </div>

          <div className="friends-content">
            {/* Search Users */}
            <section className="friends-section">
              <h3>🔍 Add Friends</h3>
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    searchUsers(e.target.value);
                  }}
                  className="search-input"
                />
              </div>

              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map(result => (
                    <div key={result.id} className="user-result">
                      <div className="user-avatar">{result.avatar || '👤'}</div>
                      <div className="user-info">
                        <div className="user-name">
                          {result.name}
                          {result.is_friend && <span className="friend-badge">✓ Friend</span>}
                          {result.has_pending_request && <span className="pending-badge">⏳ Pending</span>}
                        </div>
                        <div className="user-email">{result.email}</div>
                      </div>
                      {!result.is_friend && !result.has_pending_request && (
                        <button
                          className="add-friend-btn"
                          onClick={() => sendFriendRequest(result.email)}
                          data-testid={`add-friend-${result.id}`}
                        >
                          + Add
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Friend Requests */}
            {friendRequests.length > 0 && (
              <section className="friends-section">
                <h3>📬 Friend Requests ({friendRequests.length})</h3>
                <div className="friend-requests-list">
                  {friendRequests.map(request => (
                    <div key={request.id} className="friend-request">
                      <div className="user-avatar">{request.from_avatar || '👤'}</div>
                      <div className="user-info">
                        <div className="user-name">{request.from_name}</div>
                      </div>
                      <div className="request-actions">
                        <button
                          className="accept-btn"
                          onClick={() => acceptFriendRequest(request.id)}
                          data-testid={`accept-request-${request.id}`}
                        >
                          ✓ Accept
                        </button>
                        <button
                          className="reject-btn"
                          onClick={() => rejectFriendRequest(request.id)}
                          data-testid={`reject-request-${request.id}`}
                        >
                          ✕ Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Friends List */}
            <section className="friends-section">
              <h3>✨ My Friends ({friends.length})</h3>
              {friends.length === 0 ? (
                <div className="empty-state">
                  <p>No friends yet. Search for users to add them!</p>
                </div>
              ) : (
                <div className="friends-list">
                  {friends.map(friend => {
                    const safeAvatar = getSafeAvatar(friend.avatar);
                    const safeName = getSafeDisplayName(friend);
                    
                    return (
                      <div key={friend.user_id} className="friend-item" data-testid={`friend-${friend.user_id}`}>
                        <div className="user-avatar">
                          {safeAvatar.startsWith('data:image') || safeAvatar.startsWith('http') ? (
                            <img src={safeAvatar} alt={safeName} style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover'}} />
                          ) : (
                            safeAvatar
                          )}
                        </div>
                        <div className="user-info">
                          <div className="user-name">
                            {safeName}
                            {friend.is_online && <span className="online-dot" title="Online">🟢</span>}
                          </div>
                          <div className="user-status">
                            {friend.is_online ? 'Online' : 'Offline'}
                          </div>
                        </div>
                        <button
                          className="remove-btn"
                          onClick={() => removeFriend(friend.user_id)}
                          title="Remove friend"
                          data-testid={`remove-friend-${friend.user_id}`}
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default FriendsSystem;
